import { HttpErrorResponse } from '@angular/common/http';
import { effect, inject, Injectable, untracked } from '@angular/core';
import { buildRealtimeMonitorUrl } from '../core/config/runtime-config.provider';
import { injectRuntimeConfig } from '../core/config/runtime-config.token';
import { ConnectionFacade } from '../core/state/connection.store';
import { DriverRecord, DriversFacade } from '../core/state/drivers.store';
import { OrdersFacade, OrdersStateModel } from '../core/state/orders.store';
import { cancelLingerMsForTariffName } from '../core/state/cancel-linger';
import { resolvePartnerCountRegionIds } from '../core/state/reference-data.derived';
import { ReferenceDataFacade } from '../core/state/reference-data.store';
import { MapOverlayPreferencesService } from '../shared-integrations/map-overlay-preferences.service';
import { SessionFacade } from '../core/state/session.store';
import { RealtimeBootstrapCoordinatorService } from './realtime-bootstrap-coordinator.service';
import { MonitorBackendApiService } from '../shared-integrations/monitor-backend-api.service';
import { AirConditioningCsvService } from '../shared-integrations/air-conditioning-csv.service';
import { AudioNotificationService } from './audio-notification.service';
import { OrderTrackingService } from './order-tracking.service';
import {
  normalizeRealtimeSocketUrl,
  REALTIME_SOCKET_FACTORY,
  RealtimeSocketLike
} from './realtime-socket.token';
import {
  applyDriverLocationUpdates,
  applyNewOrderEvent,
  applyOnlineUsersUpdate,
  applyPartnerCounts,
  applyProcessingFinished,
  applyProcessingStarted,
  applyReferenceDataUpdate,
  applyUpdatedOrderEvent,
  buildDriverDetails,
  buildDriversSnapshot,
  buildDriversSnapshotFromLive,
  buildOrdersSnapshot,
  extractLegacyOrderRealtimeTariffId,
  normalizeOptions,
  normalizeRegions,
  normalizeTariffs,
  removeCancelledPendingOrders
} from './realtime-mappers';

const ORDERS_RESYNC_INTERVAL_MS = 20_000;

// Socket order events are folded into one working copy and written to the store once per window, so a burst
// (hundreds of orders at shift start or after an outage) renders a few times instead of once per event.
const ORDER_EVENT_BATCH_MS = 50;

// Driver details (tariffs, options, car, name: ~85% of the old 1.4 MB /partners answer) change rarely.
const DRIVER_DETAILS_REFRESH_MS = 60_000;
// A driver that just came online has live data but no details yet: refetch the details, at most this often.
const DRIVER_DETAILS_MISSING_RETRY_MS = 10_000;

function isSplitEndpointUnavailable(error: unknown): boolean {
  return error instanceof HttpErrorResponse && [401, 403, 404].includes(error.status);
}

export interface RealtimeMutationAck {
  error: boolean;
  error_msg?: string;
}

interface RealtimeTraceEntry {
  at: string;
  event:
    | 'newOrder'
    | 'updateOrder'
    | 'processingStarted'
    | 'processingFinished'
    | 'refreshOrders'
    | 'cancelSweep';
  orderId: string | null;
  included: boolean;
  tariffId: number | null;
  before: {
    pending: number;
    active: number;
    all: number;
    order: unknown | null;
  };
  after: {
    pending: number;
    active: number;
    all: number;
    order: unknown | null;
  };
  payload: unknown;
}

@Injectable({ providedIn: 'root' })
export class RealtimeGatewayService {
  private readonly runtimeConfig = injectRuntimeConfig();
  private readonly socketFactory = inject(REALTIME_SOCKET_FACTORY);
  private readonly sessionFacade = inject(SessionFacade);
  private readonly connectionFacade = inject(ConnectionFacade);
  private readonly referenceDataFacade = inject(ReferenceDataFacade);
  private readonly ordersFacade = inject(OrdersFacade);
  private readonly driversFacade = inject(DriversFacade);
  private readonly bootstrapCoordinator = inject(RealtimeBootstrapCoordinatorService);
  private readonly monitorBackendApi = inject(MonitorBackendApiService);
  private readonly airConditioningCsvService = inject(AirConditioningCsvService);
  private readonly orderTrackingService = inject(OrderTrackingService);
  private readonly audioNotificationService = inject(AudioNotificationService);
  private readonly mapOverlayPreferences = inject(MapOverlayPreferencesService);

  private socket: RealtimeSocketLike | null = null;
  private handledBootstrapRequestCount = 0;
  private delayedBootstrapTimeout: ReturnType<typeof setTimeout> | null = null;
  private driverLocationsBootstrapTimeout: ReturnType<typeof setTimeout> | null = null;
  private cancelledSweepInterval: ReturnType<typeof setInterval> | null = null;
  private readonly pollIntervals = new Map<string, ReturnType<typeof setInterval>>();
  private readonly pollsInFlight = new Set<string>();
  // 'unknown' until the first split refresh; 'legacy' when the backend has no /partners/details|live yet.
  private driversMode: 'unknown' | 'split' | 'legacy' = 'unknown';
  private driverDetails = new Map<string, DriverRecord>();
  private driverDetailsFetchedAt = 0;
  private lastCountRegionKey: string | null = null;
  private workingOrders: OrdersStateModel | null = null;
  private ordersFlushHandle: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    effect(() => {
      const isAuthenticated = this.sessionFacade.isAuthenticated();
      const bootstrapRequest = this.bootstrapCoordinator.lastRequest();

      if (!isAuthenticated) {
        this.shutdown();
        return;
      }

      if (!bootstrapRequest || bootstrapRequest.requestCount === this.handledBootstrapRequestCount) {
        return;
      }

      this.handledBootstrapRequestCount = bootstrapRequest.requestCount;
      void this.reconnect();
    });

    // The header counts follow the map's Regions dropdown: refetch them right away when it changes instead of
    // waiting for the next 30 s poll. The toolbar's own region change already refreshes everything, so only the map
    // selection is tracked here. The first run only records the starting selection.
    effect(() => {
      const key = this.mapOverlayPreferences.selectedRegionIds().join(',');

      untracked(() => {
        const changed = this.lastCountRegionKey !== null && this.lastCountRegionKey !== key;
        this.lastCountRegionKey = key;

        if (changed && this.sessionFacade.isAuthenticated()) {
          this.refreshPartnerCounts().catch(() => undefined);
        }
      });
    });
  }

  private partnerCountRegionIds(): number[] {
    return resolvePartnerCountRegionIds(
      this.mapOverlayPreferences.selectedRegionIds(),
      this.referenceDataFacade.effectiveRegionIds()
    );
  }

  async changeOrderAddressText(orderId: string, address: string): Promise<void> {
    const acknowledgement = await this.emitWithAcknowledgement('updateOrder', {
      action: 'changeAddressText',
      orderId,
      address
    });

    if (acknowledgement.error) {
      throw new Error(acknowledgement.error_msg || 'Cannot Change Address');
    }
  }

  async changeOrderTime(orderId: string, time: string, typeId: number): Promise<void> {
    const acknowledgement = await this.emitWithAcknowledgement('updateOrder', {
      action: 'changeTime',
      orderId,
      time,
      type: typeId
    });

    if (acknowledgement.error) {
      throw new Error(acknowledgement.error_msg || 'Cannot Update Order');
    }
  }

  async refreshMonitoringAfterMutation(includeDriverRefresh = true): Promise<void> {
    if (!this.sessionFacade.isAuthenticated()) {
      return;
    }

    const refreshes: Array<Promise<void>> = [this.refreshOrders(), this.refreshPartnerCounts()];

    if (includeDriverRefresh) {
      refreshes.push(
        this.refreshOnlineDrivers(),
        this.refreshDriverLocations(),
        this.refreshOnlineUsers()
      );
    }

    await Promise.allSettled(refreshes);
  }

  private async reconnect(): Promise<void> {
    this.flushOrders();
    this.driversMode = 'unknown';
    this.driverDetails = new Map();
    this.driverDetailsFetchedAt = 0;
    this.disposeSocket();
    this.clearBootstrapTimeouts();
    this.clearPollingIntervals();
    this.connectionFacade.setStatus('connecting');

    void this.refreshOnlineUsers();
    this.startPollingInterval('onlineUsers', 10000, () => this.refreshOnlineUsers());
    this.createSocket();
  }

  private createSocket(): void {
    const authToken = this.sessionFacade.authToken();
    const userId = this.sessionFacade.userId();

    if (!authToken || !userId) {
      return;
    }

    const socketUrl = normalizeRealtimeSocketUrl(buildRealtimeMonitorUrl(this.runtimeConfig));

    this.connectionFacade.updateDebug({
      socketUrl,
      socketPath: '/socket.io',
      authUserId: userId,
      authTokenPreview: `${authToken.slice(0, 8)}...`,
      engineProtocol: 'EIO3 compatibility enabled',
      lastSocketEvent: 'createSocket'
    });

    this.socket = this.socketFactory(socketUrl, {
      transports: this.runtimeConfig.realtime.transports,
      token: authToken,
      userId
    });

    this.socket.on('connect', () => {
      this.recordSocketDebug('connect');
      void this.handleConnect();
    });
    this.socket.on('connecting', () => {
      this.recordSocketDebug('connecting');
      this.connectionFacade.setStatus('connecting');
    });
    this.socket.on('connect_failed', (payload) => {
      this.recordSocketDebug('connect_failed', payload);
      this.connectionFacade.setStatus('connecting', this.formatSocketError(payload));
    });
    this.socket.on('connect_error', (payload) => {
      this.recordSocketDebug('connect_error', payload);
      this.connectionFacade.setStatus('connecting', this.formatSocketError(payload));
    });
    this.socket.on('reconnecting', (payload) => {
      this.recordSocketDebug('reconnecting', payload);
      this.connectionFacade.incrementReconnectAttempt();
    });
    this.socket.on('reconnect_failed', (payload) => {
      this.recordSocketDebug('reconnect_failed', payload);
      this.connectionFacade.setStatus('error', this.formatSocketError(payload));
    });
    this.socket.on('disconnect', (payload) => {
      this.recordSocketDebug('disconnect', payload);
      if (this.sessionFacade.isAuthenticated()) {
        this.connectionFacade.setStatus('connecting');
        return;
      }

      this.connectionFacade.setStatus('offline');
    });
    this.socket.on('newOrder', (payload) => {
      this.handleNewOrder(payload);
    });
    this.socket.on('updateOrder', (payload) => {
      this.handleUpdatedOrder(payload);
    });
    this.socket.on('processingStarted', (payload) => {
      this.handleProcessingStarted(payload);
    });
    this.socket.on('processingFinished', (payload) => {
      this.handleProcessingFinished(payload);
    });

    this.socket.connect();
  }

  private async handleConnect(): Promise<void> {
    this.connectionFacade.setStatus('online');
    this.ordersFacade.setTrackingState(this.orderTrackingService.initializeTrackingState());

    void Promise.allSettled([
      this.airConditioningCsvService.load(),
      this.refreshRegions(),
      this.refreshTariffs(),
      this.refreshOptions()
    ]);

    this.startCancelledSweep();
    this.schedulePostConnectBootstrap();
  }

  private schedulePostConnectBootstrap(): void {
    this.clearBootstrapTimeouts();

    this.delayedBootstrapTimeout = setTimeout(() => {
      void this.refreshOnlineDrivers();
      void this.refreshOnlineUsers();
      void this.refreshOrders();
      void this.refreshPartnerCounts();
      this.startPollingInterval('drivers', 15000, () => this.refreshOnlineDrivers());
      this.startPollingInterval('partnerCounts', 30000, () => this.refreshPartnerCounts());
      // Safety net for socket events that never arrive: without it a missed accept leaves the
      // order in Pending until the page is reloaded. The backend caches this list briefly.
      this.startPollingInterval('orders', ORDERS_RESYNC_INTERVAL_MS, () => this.refreshOrders());
    }, 1000);

    this.driverLocationsBootstrapTimeout = setTimeout(() => {
      void this.refreshDriverLocations();
      this.startPollingInterval('driverLocations', 10000, () => this.refreshDriverLocations());
    }, 20000);
  }

  private async refreshRegions(): Promise<void> {
    const rawRegions = await this.monitorBackendApi.getRegions();
    const currentState = this.referenceDataFacade.referenceData();
    this.referenceDataFacade.setReferenceData(
      applyReferenceDataUpdate(currentState, {
        regions: normalizeRegions(rawRegions)
      })
    );
  }

  private async refreshTariffs(): Promise<void> {
    const rawTariffs = await this.monitorBackendApi.getTariffs();
    const currentState = this.referenceDataFacade.referenceData();
    this.referenceDataFacade.setReferenceData(
      applyReferenceDataUpdate(currentState, {
        tariffs: normalizeTariffs(rawTariffs)
      })
    );
  }

  private async refreshOptions(): Promise<void> {
    const rawOptions = await this.monitorBackendApi.getOptions();
    const currentState = this.referenceDataFacade.referenceData();
    this.referenceDataFacade.setReferenceData(
      applyReferenceDataUpdate(currentState, {
        options: normalizeOptions(rawOptions)
      })
    );
  }

  private async refreshOrders(): Promise<void> {
    const rawOrders = await this.monitorBackendApi.getOrders();
    const filteredOrders = rawOrders.filter((rawOrder) =>
      this.referenceDataFacade.matchesTariffId(extractLegacyOrderRealtimeTariffId(rawOrder))
    );

    // Read the state AFTER the request, never before it. buildOrdersSnapshot carries this
    // state forward (selectedOrderId included), so a value captured before the await would
    // patch a stale selection back over the operator's newer click. That is what made the
    // view jump back to the previous order when one was picked while an attach — which
    // triggers this refresh immediately — was still in flight.
    this.flushOrders();
    const beforeState = this.ordersFacade.orders();
    const nextState = buildOrdersSnapshot(filteredOrders, beforeState);
    this.ordersFacade.replaceSnapshot(nextState);
    this.recordRealtimeTrace(
      'refreshOrders',
      {
        total: rawOrders.length,
        filtered: filteredOrders.length
      },
      null,
      null,
      true,
      beforeState,
      nextState
    );
  }

  private async refreshOnlineDrivers(): Promise<void> {
    if (this.driversMode === 'legacy') {
      return this.refreshOnlineDriversLegacy();
    }

    try {
      const detailsDue =
        this.driverDetails.size === 0 || Date.now() - this.driverDetailsFetchedAt > DRIVER_DETAILS_REFRESH_MS;
      const [rawLive, rawDetails] = await Promise.all([
        this.monitorBackendApi.getPartnerLive(),
        detailsDue ? this.monitorBackendApi.getPartnerDetails() : Promise.resolve(null)
      ]);

      if (rawDetails) {
        this.driverDetails = buildDriverDetails(rawDetails);
        this.driverDetailsFetchedAt = Date.now();
      }

      this.driversMode = 'split';
      let result = buildDriversSnapshotFromLive(rawLive, this.driverDetails, this.driversFacade.drivers());

      if (
        result.missingDetailsCount > 0 &&
        Date.now() - this.driverDetailsFetchedAt > DRIVER_DETAILS_MISSING_RETRY_MS
      ) {
        this.driverDetails = buildDriverDetails(await this.monitorBackendApi.getPartnerDetails());
        this.driverDetailsFetchedAt = Date.now();
        result = buildDriversSnapshotFromLive(rawLive, this.driverDetails, this.driversFacade.drivers());
      }

      this.driversFacade.replaceSnapshot(result.state);
    } catch (error) {
      // Backend without the split endpoints (not deployed yet, or the route is not granted): use /partners.
      if (this.driversMode === 'unknown' && isSplitEndpointUnavailable(error)) {
        this.driversMode = 'legacy';
        return this.refreshOnlineDriversLegacy();
      }

      throw error;
    }
  }

  private async refreshOnlineDriversLegacy(): Promise<void> {
    const rawDrivers = await this.monitorBackendApi.getPartners();
    this.driversFacade.replaceSnapshot(buildDriversSnapshot(rawDrivers, this.driversFacade.drivers()));
  }

  private async refreshOnlineUsers(): Promise<void> {
    const rawUsers = await this.monitorBackendApi.getOnlineUsers();
    this.driversFacade.replaceSnapshot(applyOnlineUsersUpdate(this.driversFacade.drivers(), rawUsers));
  }

  private async refreshPartnerCounts(): Promise<void> {
    const rawCounts = await this.monitorBackendApi.getPartnerCounts(
      this.partnerCountRegionIds(),
      this.referenceDataFacade.effectiveCarTypeIds()
    );

    this.driversFacade.replaceSnapshot(applyPartnerCounts(this.driversFacade.drivers(), rawCounts));
  }

  private async refreshDriverLocations(): Promise<void> {
    const rawLocations = await this.monitorBackendApi.getPartnerLocations();
    this.driversFacade.replaceSnapshot(
      applyDriverLocationUpdates(this.driversFacade.drivers(), rawLocations)
    );
  }

  private currentOrders(): OrdersStateModel {
    return this.workingOrders ?? this.ordersFacade.orders();
  }

  private commitOrders(next: OrdersStateModel): void {
    this.workingOrders = next;

    if (this.ordersFlushHandle === null) {
      this.ordersFlushHandle = setTimeout(() => this.flushOrders(), ORDER_EVENT_BATCH_MS);
    }
  }

  private flushOrders(): void {
    if (this.ordersFlushHandle !== null) {
      clearTimeout(this.ordersFlushHandle);
      this.ordersFlushHandle = null;
    }

    if (this.workingOrders !== null) {
      const next = this.workingOrders;
      this.workingOrders = null;
      this.ordersFacade.replaceSnapshot(next);
    }
  }

  private handleNewOrder(payload: unknown): void {
    const beforeState = this.currentOrders();
    const tariffId = extractLegacyOrderRealtimeTariffId(payload);
    const included = this.referenceDataFacade.matchesTariffId(tariffId);
    const orderId = this.extractOrderIdFromOrderPayload(payload);

    if (!included) {
      this.recordRealtimeTrace('newOrder', payload, orderId, tariffId, false, beforeState, beforeState);
      return;
    }

    const nextState = applyNewOrderEvent(beforeState, payload);
    this.commitOrders(nextState);
    this.recordRealtimeTrace('newOrder', payload, orderId, tariffId, true, beforeState, nextState);
    this.audioNotificationService.playNewOrderNotification();
  }

  private handleUpdatedOrder(payload: unknown): void {
    const beforeState = this.currentOrders();
    const payloadRecord =
      typeof payload === 'object' && payload !== null ? (payload as Record<string, unknown>) : null;
    const orderPayload = payloadRecord?.['order'] ?? payload;
    const tariffId = extractLegacyOrderRealtimeTariffId(orderPayload);
    const included = this.referenceDataFacade.matchesTariffId(tariffId);
    const orderId = this.extractOrderIdFromOrderPayload(orderPayload);

    if (!included) {
      this.recordRealtimeTrace('updateOrder', payload, orderId, tariffId, false, beforeState, beforeState);
      return;
    }

    const nextState = applyUpdatedOrderEvent(beforeState, payload);
    this.commitOrders(nextState);
    this.recordRealtimeTrace('updateOrder', payload, orderId, tariffId, true, beforeState, nextState);

    if (this.hasCancelledOrder(payload)) {
      this.audioNotificationService.playCancelledOrderNotification();
    }
  }

  private handleProcessingStarted(payload: unknown): void {
    const beforeState = this.currentOrders();
    const orderId = this.extractOrderId(payload);

    if (!orderId) {
      this.recordRealtimeTrace('processingStarted', payload, null, null, false, beforeState, beforeState);
      return;
    }

    const nextState = applyProcessingStarted(beforeState, orderId);
    this.commitOrders(nextState);
    this.recordRealtimeTrace('processingStarted', payload, orderId, null, true, beforeState, nextState);
  }

  private handleProcessingFinished(payload: unknown): void {
    const beforeState = this.currentOrders();
    const orderId = this.extractOrderId(payload);

    if (!orderId) {
      this.recordRealtimeTrace('processingFinished', payload, null, null, false, beforeState, beforeState);
      return;
    }

    const nextState = applyProcessingFinished(beforeState, orderId);
    this.commitOrders(nextState);
    this.recordRealtimeTrace('processingFinished', payload, orderId, null, true, beforeState, nextState);
  }

  private emitWithAcknowledgement(
    event: string,
    payload: Record<string, unknown>
  ): Promise<RealtimeMutationAck> {
    return new Promise<RealtimeMutationAck>((resolve, reject) => {
      if (!this.socket) {
        reject(new Error('Realtime connection is not ready.'));
        return;
      }

      this.socket.emit(event, payload, (response) => {
        const ackRecord =
          typeof response === 'object' && response !== null
            ? (response as Record<string, unknown>)
            : {};

        resolve({
          error: ackRecord['error'] === true,
          error_msg: typeof ackRecord['error_msg'] === 'string' ? ackRecord['error_msg'] : undefined
        });
      });
    });
  }

  private startCancelledSweep(): void {
    if (this.cancelledSweepInterval) {
      return;
    }

    this.cancelledSweepInterval = setInterval(() => {
      const beforeState = this.ordersFacade.orders();
      const tariffLookup = this.referenceDataFacade.tariffLookup();
      const nextState = removeCancelledPendingOrders(beforeState, (order) =>
        cancelLingerMsForTariffName(order.tariffId === null ? null : tariffLookup[order.tariffId]?.name)
      );
      this.ordersFacade.replaceSnapshot(nextState);
      this.recordRealtimeTrace('cancelSweep', null, null, null, true, beforeState, nextState);
    }, 3000);
  }

  // Skips a tick while the previous call for the same key is still running, so a slow backend is
  // not hit with overlapping copies of the same (large) request.
  private startPollingInterval(
    key: string,
    intervalMs: number,
    callback: () => Promise<unknown> | void
  ): void {
    if (this.pollIntervals.has(key)) {
      return;
    }

    this.pollIntervals.set(
      key,
      setInterval(() => {
        if (this.pollsInFlight.has(key)) {
          return;
        }

        this.pollsInFlight.add(key);
        Promise.resolve()
          .then(callback)
          .catch(() => undefined)
          .finally(() => this.pollsInFlight.delete(key));
      }, intervalMs)
    );
  }

  private extractOrderId(payload: unknown): string | null {
    if (typeof payload === 'string' && payload.length > 0) {
      try {
        const parsedPayload = JSON.parse(payload);
        return this.extractOrderId(parsedPayload);
      } catch {
        return payload;
      }
    }

    if (typeof payload === 'number' && Number.isFinite(payload)) {
      return `${payload}`;
    }

    if (typeof payload === 'object' && payload !== null) {
      const record = payload as { order?: unknown; orderId?: unknown };

      for (const value of [record.order, record.orderId]) {
        if (typeof value === 'string' && value.length > 0) {
          return value;
        }

        if (typeof value === 'number' && Number.isFinite(value)) {
          return `${value}`;
        }
      }
    }

    return null;
  }

  private extractOrderIdFromOrderPayload(payload: unknown): string | null {
    const parsedPayload =
      typeof payload === 'string' && payload.length > 0 ? this.tryParseJson(payload) ?? payload : payload;

    if (typeof parsedPayload === 'object' && parsedPayload !== null) {
      const record = parsedPayload as Record<string, unknown>;
      const value = record['orderId'];

      if (typeof value === 'string' && value.length > 0) {
        return value;
      }

      if (typeof value === 'number' && Number.isFinite(value)) {
        return `${value}`;
      }
    }

    return this.extractOrderId(parsedPayload);
  }

  private hasCancelledOrder(payload: unknown): boolean {
    if (typeof payload !== 'object' || payload === null || !('order' in payload)) {
      return false;
    }

    const orderPayload = (payload as { order?: unknown }).order;
    const parsedOrder =
      typeof orderPayload === 'string'
        ? (JSON.parse(orderPayload) as { cancelDate?: unknown })
        : (orderPayload as { cancelDate?: unknown } | undefined);

    return Boolean(parsedOrder?.cancelDate);
  }

  private tryParseJson(value: string): unknown | null {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  private recordRealtimeTrace(
    event: RealtimeTraceEntry['event'],
    payload: unknown,
    orderId: string | null,
    tariffId: number | null,
    included: boolean,
    beforeState = this.ordersFacade.orders(),
    afterState = this.ordersFacade.orders()
  ): void {
    const target = globalThis as typeof globalThis & {
      __WM_REALTIME_TRACE__?: RealtimeTraceEntry[];
    };

    const entry: RealtimeTraceEntry = {
      at: new Date().toISOString(),
      event,
      orderId,
      included,
      tariffId,
      before: {
        pending: beforeState.pendingIds.length,
        active: beforeState.activeIds.length,
        all: beforeState.allIds.length,
        order: orderId ? beforeState.byId[orderId] ?? null : null
      },
      after: {
        pending: afterState.pendingIds.length,
        active: afterState.activeIds.length,
        all: afterState.allIds.length,
        order: orderId ? afterState.byId[orderId] ?? null : null
      },
      payload
    };

    const trace = target.__WM_REALTIME_TRACE__ ?? [];
    trace.push(entry);

    if (trace.length > 200) {
      trace.splice(0, trace.length - 200);
    }

    target.__WM_REALTIME_TRACE__ = trace;
  }

  private formatSocketError(payload: unknown): string | null {
    if (payload instanceof Error) {
      return payload.message;
    }

    if (typeof payload === 'string') {
      return payload;
    }

    return null;
  }

  private shutdown(): void {
    if (this.ordersFlushHandle !== null) {
      clearTimeout(this.ordersFlushHandle);
      this.ordersFlushHandle = null;
    }
    this.workingOrders = null;
    this.disposeSocket();
    this.clearBootstrapTimeouts();
    this.clearPollingIntervals();

    if (this.cancelledSweepInterval) {
      clearInterval(this.cancelledSweepInterval);
      this.cancelledSweepInterval = null;
    }
  }

  private disposeSocket(): void {
    if (!this.socket) {
      return;
    }

    this.recordSocketDebug('disposeSocket');
    this.socket.removeAllListeners();
    this.socket.disconnect();
    this.socket = null;
  }

  private recordSocketDebug(eventName: string, payload?: unknown): void {
    this.connectionFacade.updateDebug({
      lastSocketEvent: eventName,
      engineProtocol: this.readSocketEngineProtocol(),
      lastSocketEventAt: new Date().toISOString()
    });

    const errorMessage = this.formatSocketError(payload);

    if (errorMessage) {
      this.connectionFacade.setStatus(this.connectionFacade.status(), errorMessage);
    }
  }

  private readSocketEngineProtocol(): string | null {
    const socketRecord = this.socket as
      | ({
          io?: {
            engine?: {
              protocol?: number;
            };
          };
        } & RealtimeSocketLike)
      | null;
    const protocol = socketRecord?.io?.engine?.protocol;

    return typeof protocol === 'number' ? `EIO=${protocol}` : 'EIO3 compatibility enabled';
  }

  private clearBootstrapTimeouts(): void {
    if (this.delayedBootstrapTimeout) {
      clearTimeout(this.delayedBootstrapTimeout);
      this.delayedBootstrapTimeout = null;
    }

    if (this.driverLocationsBootstrapTimeout) {
      clearTimeout(this.driverLocationsBootstrapTimeout);
      this.driverLocationsBootstrapTimeout = null;
    }
  }

  private clearPollingIntervals(): void {
    for (const handle of this.pollIntervals.values()) {
      clearInterval(handle);
    }

    this.pollIntervals.clear();
    this.pollsInFlight.clear();
  }
}
